import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { Card, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRouteParams } from '@/contexts/RouteContext';
import type {
	ScenarioViewModel,
	UpdateScenarioMutation,
	UpdateScenarioMutationVariables
} from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import { validateAllConditionsHaveActions } from '@/lib/conditionValidation';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';
import { useMutation } from '@apollo/client';
import { Building, Filter, Loader2, Plus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ConditionCard } from './ConditionCard';
import { ConditionEditor } from './ConditionEditor';
import {
	buildUpdateScenarioInput,
	type ScenarioConditionFormState
} from './scenario-input';
import { SCENARIO_QUERY, SCENARIO_UPDATE_MUTATION } from './scenarios.gql';

interface EditScenarioFormData {
	name: string;
	facilityId?: string;
}

// Using ScenarioConditionFormState from scenario-input.ts

export const ScenariosEdit: React.FC = () => {
	const { t } = useTranslation('scenarios');
	const go = useGo();
	const { id } = useRouteParams();
	useEditPageHeader({ id, resourceName: 'scenarios' });
	const dataLoadedRef = useRef(false);

	// Update scenario mutation
	const [updateScenario, { loading: isMutationSubmitting }] = useMutation<
		UpdateScenarioMutation,
		UpdateScenarioMutationVariables
	>(SCENARIO_UPDATE_MUTATION);

	const [selectedConditions, setSelectedConditions] = useState<
		ScenarioConditionFormState[]
	>([]);
	const [conditionEditorOpen, setConditionEditorOpen] = useState(false);
	const [editingCondition, setEditingCondition] = useState<{
		condition: ScenarioConditionFormState;
		index: number;
	} | null>(null);
	const [conditionOperator, setConditionOperator] = useState<
		'include' | 'exclude'
	>('include');
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Fetch existing scenario data
	const {
		data: scenarioData,
		isLoading,
		error
	} = useOne<ScenarioViewModel>({
		resource: 'scenarios',
		id: id!,
		meta: {
			gqlQuery: SCENARIO_QUERY
		}
	});

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm<EditScenarioFormData>({
		defaultValues: {
			name: ''
		}
	});

	useUnsavedChanges(control._formState.isDirty, {
		isSubmitSuccessful: control._formState.isSubmitSuccessful,
		isSubmitting
	});

	// Convert existing scenario conditions to form data when scenario loads
	useEffect(() => {
		if (scenarioData?.data && !dataLoadedRef.current) {
			const scenario = scenarioData.data;

			// Reset form with scenario data
			reset({
				name: scenario.name,
				facilityId: scenario.facilityId
			});

			// Convert existing conditions to form format
			const convertedConditions: ScenarioConditionFormState[] =
				scenario.scenarioConditions?.map(sc => ({
					conditionId: sc.conditionId,
					name: undefined, // Backend condition doesn't include name for display
					startDate: sc.condition.startDate
						? new Date(sc.condition.startDate)
								.toISOString()
								.split('T')[0]
						: undefined,
					endDate: sc.condition.endDate
						? new Date(sc.condition.endDate)
								.toISOString()
								.split('T')[0]
						: undefined,
					arrivingSince: sc.condition.arrivingSince || undefined,
					arrivingUntil: sc.condition.arrivingUntil || undefined,
					leavingSince: sc.condition.leavingSince || undefined,
					leavingUntil: sc.condition.leavingUntil || undefined,
					weeklyDateSetId: sc.condition.weeklyDateSetId || undefined,
					appliesOnHolidays: sc.condition.appliesOnHolidays || false,
					minStayMinutes: sc.condition.minStayMinutes || undefined,
					maxStayMinutes: sc.condition.maxStayMinutes || undefined,
					appliesToSpecificVehicleTypes:
						sc.condition.appliesToSpecificVehicleTypes || false,
					appliesToSpecificCustomerCategories:
						sc.condition.appliesToSpecificCustomerCategories ||
						false,
					appliesToSpecificPaymentMethods:
						sc.condition.appliesToSpecificPaymentMethods || false,
					isOverstay: sc.condition.isOverstay || false,
					isContractOverstay:
						sc.condition.isContractOverstay || false,
					selectedVehicleTypes:
						sc.condition.conditionVehicleTypes?.map(vt =>
							vt.vehicleTypeId.toString()
						) || [],
					selectedCustomerCategories:
						sc.condition.conditionCustomerCategories?.map(cc =>
							cc.customerCategoryId.toString()
						) || [],
					selectedPaymentMethods:
						sc.condition.conditionPaymentMethods?.map(pm =>
							pm.paymentMethodId.toString()
						) || [],
					selectedDaysOfWeek:
						sc.condition.conditionDayOfWeeks?.map(dow =>
							dow.dayOfWeekId.toString()
						) || [],
					operator: 'include' // Default to include for existing conditions
				})) || [];

			setSelectedConditions(convertedConditions);
			dataLoadedRef.current = true;
		}
	}, [id, isLoading, scenarioData?.data?.scenarioId]); // Include scenarioId to reset when switching between scenarios

	const addCondition = (conditionData: ScenarioConditionFormState) => {
		setSelectedConditions(prev => [...prev, conditionData]);
		setConditionEditorOpen(false);
	};

	const updateCondition = (conditionData: ScenarioConditionFormState) => {
		if (editingCondition) {
			setSelectedConditions(prev =>
				prev.map((c, index) =>
					index === editingCondition.index ? conditionData : c
				)
			);
			setEditingCondition(null);
		}
		setConditionEditorOpen(false);
	};

	const removeCondition = (index: number) => {
		setSelectedConditions(prev => prev.filter((_, i) => i !== index));
	};

	const toggleConditionOperator = (index: number) => {
		setSelectedConditions(prev =>
			prev.map((c, i) =>
				i === index
					? {
							...c,
							operator:
								c.operator === 'include' ? 'exclude' : 'include'
						}
					: c
			)
		);
	};

	const openConditionEditor = (
		operator: 'include' | 'exclude' = 'include'
	) => {
		setConditionOperator(operator);
		setEditingCondition(null);
		setConditionEditorOpen(true);
	};

	const openConditionEditorForEdit = (
		condition: ScenarioConditionFormState,
		index: number
	) => {
		setEditingCondition({ condition, index });
		setConditionOperator(condition.operator);
		setConditionEditorOpen(true);
	};

	const onSubmit = async (data: EditScenarioFormData) => {
		setIsSubmitting(true);

		try {
			// Validate that all conditions have meaningful actions
			const validation =
				validateAllConditionsHaveActions(selectedConditions);

			if (!validation.isValid) {
				toast.error(
					t('form.cannotSaveScenarioError', {
						ruleNumbers: validation.invalidConditions.join(', #'),
						count: validation.invalidConditions.length
					}),
					{
						duration: 6000
					}
				);
				return;
			}

			const input = buildUpdateScenarioInput(
				{
					name: data.name,
					conditions: selectedConditions
				},
				id!
			);

			const { data: response } = await updateScenario(
				getMutationOptions('scenarios', { input })
			);

			const message = extractCheckResultError(response?.updateScenario);

			if (message) {
				showMutationErrorToast('scenarios', 'update', { message });
				return;
			}

			showMutationSuccessToast('scenarios', 'update');
			// Navigate back with the edited scenario ID for selection
			go({
				to: '/scenarios',
				state: { selectScenarioId: id }
			});
		} catch (error) {
			showMutationErrorToast('scenarios', 'update', { error });
		} finally {
			setIsSubmitting(false);
		}
	};

	// Separate conditions by operator
	const includeConditions = selectedConditions.filter(
		c => c.operator === 'include'
	);
	const excludeConditions = selectedConditions.filter(
		c => c.operator === 'exclude'
	);

	if (isLoading) {
		return (
			<FormPageLayout>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="w-8 h-8 animate-spin" />
					<span className="ml-2">{t('form.loadingScenario')}</span>
				</div>
			</FormPageLayout>
		);
	}

	if (error) {
		return (
			<FormPageLayout>
				<div className="py-12 text-center">
					<p className="text-red-500">
						{t('form.errorLoadingScenario')}: {error.message}
					</p>
					<Button
						onClick={() => go({ to: '/scenarios' })}
						className="mt-4"
					>
						{t('form.backToScenarios')}
					</Button>
				</div>
			</FormPageLayout>
		);
	}

	return (
		<FormPageLayout>
			<FormHeaderWithStatus
				title={t('edit.title')}
				rightContent={
					<FormActionButtons
						isSubmitting={isSubmitting || isMutationSubmitting}
						submitLabel={t('form.updateScenarioButton')}
						formId="scenario-form"
					/>
				}
			/>

			{/* Form */}
			<form
				id="scenario-form"
				onSubmit={handleSubmit(onSubmit)}
				className="space-y-6"
			>
				{/* Basic Information */}
				<Card>
					<CardTitle className="flex items-center gap-2">
						<Building className="w-5 h-5" />
						{t('form.basicInformation')}
					</CardTitle>

					{t('form.editBasicInformationDesc')}

					<div className="space-y-2">
						<Label htmlFor="name">
							{t('form.name')} *
						</Label>
						<Controller
							name="name"
							control={control}
							rules={{
								required: t('form.scenarioNameRequired')
							}}
							render={({ field }) => (
								<Input
									{...field}
									id="name"
									placeholder={t(
										'form.scenarioNamePlaceholder'
									)}
									className={
										errors.name
											? 'border-red-500'
											: ''
									}
								/>
							)}
						/>
						{errors.name && (
							<p className="text-sm text-red-500">
								{errors.name.message}
							</p>
						)}
					</div>
				</Card>

				{/* Conditions Management */}
				<Card>
					<CardTitle className="flex items-center gap-2">
						<Filter className="w-5 h-5" />
						{t('form.scenarioRules')}
					</CardTitle>
					{t('form.scenarioConditionsDesc')}
					{/* Action Buttons */}
					<div className="flex gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={() => openConditionEditor('include')}
						>
							<Plus className="w-4 h-4 mr-2" />
							{t('form.addIncludeRules')}
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => openConditionEditor('exclude')}
						>
							<Plus className="w-4 h-4 mr-2" />
							{t('form.addExcludeRules')}
						</Button>
					</div>{' '}
					{/* Selected Conditions Display */}
					{selectedConditions.length === 0 ? (
						<div className="py-12 text-center border-2 border-gray-200 border-dashed rounded-lg">
							<Filter className="w-12 h-12 mx-auto mb-4 text-gray-400" />
							<h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">
								{t('form.noConditionsDefined')}
							</h3>
							<p className="mb-4 text-gray-500">
								{t('form.noConditionsDefinedDesc')}
							</p>
							<Button
								type="button"
								variant="outline"
								onClick={() => openConditionEditor('include')}
							>
								<Plus className="w-4 h-4 mr-2" />
								{t('form.addYourFirstCondition')}
							</Button>
						</div>
					) : (
						<div className="space-y-6">
							{/* Include Conditions */}
							{includeConditions.length > 0 && (
								<div>
									<div className="flex items-center gap-2 mb-4">
										<Badge className="text-green-800 bg-green-100 border-green-200">
											{t('form.includeConditions')} (
											{includeConditions.length})
										</Badge>
										<span className="text-sm text-gray-600">
											{t('form.includeConditionsDesc')}
										</span>
									</div>
									<div className="space-y-3">
										{includeConditions.map(condition => {
											const globalIndex =
												selectedConditions.findIndex(
													c => c === condition
												);
											return (
												<ConditionCard
													key={globalIndex}
													condition={condition}
													onEdit={() =>
														openConditionEditorForEdit(
															condition,
															globalIndex
														)
													}
													onDelete={() =>
														removeCondition(
															globalIndex
														)
													}
													onToggleOperator={() =>
														toggleConditionOperator(
															globalIndex
														)
													}
												/>
											);
										})}
									</div>
								</div>
							)}

							{/* Exclude Conditions */}
							{excludeConditions.length > 0 && (
								<div>
									<div className="flex items-center gap-2 mb-4">
										<Badge className="text-red-800 bg-red-100 border-red-200">
											{t('form.excludeConditions')} (
											{excludeConditions.length})
										</Badge>
										<span className="text-sm text-gray-600">
											{t('form.excludeConditionsDesc')}
										</span>
									</div>
									<div className="space-y-3">
										{excludeConditions.map(condition => {
											const globalIndex =
												selectedConditions.findIndex(
													c => c === condition
												);
											return (
												<ConditionCard
													key={globalIndex}
													condition={condition}
													onEdit={() =>
														openConditionEditorForEdit(
															condition,
															globalIndex
														)
													}
													onDelete={() =>
														removeCondition(
															globalIndex
														)
													}
													onToggleOperator={() =>
														toggleConditionOperator(
															globalIndex
														)
													}
												/>
											);
										})}
									</div>
								</div>
							)}

							{/* Logic Summary */}
							<div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
								<h4 className="mb-2 font-medium">
									{t('form.logicSummary')}
								</h4>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									{t('form.logicSummaryDesc')}
									{includeConditions.length > 0 && (
										<>
											<br />•{' '}
											{t('form.anyOfInclude', {
												count: includeConditions.length
											})}
										</>
									)}
									{excludeConditions.length > 0 && (
										<>
											<br />•{' '}
											{t('form.noneOfExclude', {
												count: excludeConditions.length
											})}
										</>
									)}
									{selectedConditions.length === 0 && (
										<>
											<br />•{' '}
											{t('form.noConditionsWillNotApply')}
										</>
									)}
								</p>
							</div>
						</div>
					)}
				</Card>
			</form>

			{/* Condition Editor Dialog */}
			<ConditionEditor
				open={conditionEditorOpen}
				mode={editingCondition ? 'edit' : 'create'}
				condition={editingCondition?.condition}
				operator={conditionOperator}
				onSave={editingCondition ? updateCondition : addCondition}
				onCancel={() => {
					setConditionEditorOpen(false);
					setEditingCondition(null);
				}}
			/>
		</FormPageLayout>
	);
};
